import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays, Clock, Gift, User, LogOut,
  ChevronRight, AlertTriangle, Check, MapPin, Phone,
} from 'lucide-react';
import { supabase } from '../config/supabase';
import supabaseService from '../services/supabaseService';
import '../assets/styles/account.css';

const SALON_PHONE = '021 881 498';
const SALON_EMAIL = 'viktoriashouseofbeauty@gmail.com';

const GOOGLE_PENDING_KEY = 'bbv.google-signin';

// Saved or skipped, the welcome block stays out of the way for the rest of the
// browsing session. It comes back on the next visit if anything is still
// missing — asking twice a month is a nudge, asking on every page load is
// nagging.
// Keyed per account and held in localStorage: "ask once" should mean once for
// this person, not once per browser tab. sessionStorage survived the Google
// redirect (so a dismissal during sign-up hid it forever afterwards) while being
// forgotten in a new tab -- backwards on both counts.
const welcomeDoneKey = (userId) => `bbv.welcome-done:${userId || 'anon'}`;

// Plumbing that failed, not a person who failed you: nobody says "I couldn't
// finish signing you in with Google" out loud. The actor is left out rather than
// swapped for a company that does not exist.
const GOOGLE_FAILED =
  'That Google sign-in didn\'t finish. Have another go, or use your email and password below.';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Leap-safe: 29 February is a real birthday even though it is not a real date
// most years.
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/* ── formatting ────────────────────────────────────────────────────────── */

const formatMoney = (cents) => {
  const value = (Number(cents) || 0) / 100;
  return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  // en-NZ renders "Sat, 2 Aug 2026"; the comma is dropped to match the site.
  return d
    .toLocaleDateString('en-NZ', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    })
    .replace(/,/g, '');
};

const formatTime = (time) => {
  if (!time) return '';
  const [h, m] = String(time).split(':');
  const hour = parseInt(h, 10);
  if (Number.isNaN(hour)) return String(time);
  return `${hour % 12 || 12}:${m}${hour >= 12 ? 'pm' : 'am'}`;
};

const treatmentNames = (booking) =>
  (booking.treatments || []).map((t) => t.name).filter(Boolean).join(', ');

const bookingDuration = (booking) =>
  booking.total_duration ||
  (booking.treatments || []).reduce((sum, t) => sum + (t.duration_minutes || 0), 0);

// Bookings taken before prices moved to cents still only have the display string.
const bookingPrice = (booking) =>
  booking.treatment_cents > 0 ? formatMoney(booking.treatment_cents) : booking.total_price || '';

// `created_at` is a real instant, not one of the site's local YYYY-MM-DD dates,
// so it can be compared directly. Only used to decide whether the greeting is
// allowed to say "welcome" — an old account missing a birthday gets the same
// block worded plainly instead.
const isNewAccount = (createdAt) => {
  const started = Date.parse(createdAt);
  return !Number.isNaN(started) && Date.now() - started < 7 * 24 * 60 * 60 * 1000;
};

const sortByWhen = (a, b) =>
  `${a.date}T${a.time || '00:00'}`.localeCompare(`${b.date}T${b.time || '00:00'}`);

/* ── signing in with Google ────────────────────────────────────────────── */

// Session storage is unavailable in some privacy modes, and none of this is
// important enough to break sign-in over.
// The welcome flag needs to outlive the tab; the Google-pending flag must not.
const readPersistent = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (err) {
    return null;
  }
};
const writePersistent = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (err) {
    /* private browsing can refuse; the worst case is being asked once more */
  }
};

const readFlag = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    return null;
  }
};
const writeFlag = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    /* nothing to do — the flag is only used to word the message on the way back */
  }
};
const clearFlag = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (err) {
    /* as above */
  }
};

// Google and Supabase both hand back machine-readable reasons. None of them mean
// anything to a client, so each one becomes a sentence that says what happened
// and what to do next.
function oauthMessage(code, description) {
  const raw = `${code} ${description}`.toLowerCase();
  if (raw.includes('access_denied') || raw.includes('cancel')) {
    return 'That Google sign-in was cancelled, so nothing has changed. Try again, or sign in '
      + 'with your email and password below.';
  }
  if (raw.includes('provider is not enabled') || raw.includes('unsupported_provider')) {
    return 'Signing in with Google isn\'t switched on yet. Please use your email and password '
      + 'below.';
  }
  if (raw.includes('already registered') || raw.includes('already been registered')) {
    return 'There\'s already an account with that email address. Sign in with your email and '
      + 'password below instead.';
  }
  return GOOGLE_FAILED;
}

// Read once, as this module loads. supabase-js strips the tokens and the error
// out of the URL as soon as it has finished with them, which can happen before
// any effect on this page gets a chance to look.
function readOAuthEntry() {
  if (typeof window === 'undefined') return { started: false, error: '' };

  const started = readFlag(GOOGLE_PENDING_KEY) === '1';
  if (started) clearFlag(GOOGLE_PENDING_KEY);

  // Implicit flow puts the failure in the hash; a PKCE redirect puts it in the
  // query string. Both are cheap to check.
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const code = hash.get('error') || query.get('error');
  const description = hash.get('error_description') || query.get('error_description') || '';

  return { started, error: code ? oauthMessage(code, description) : '' };
}

const OAUTH_ENTRY = readOAuthEntry();

// Google's brand guidelines require their own mark on the button, and no page on
// this site is allowed to fetch anything from a third party, so it is inlined.
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.28-3.14.76-4.59l-7.97-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/* ── shared bits ───────────────────────────────────────────────────────── */

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="acc-section">
      <div className="acc-section-head">
        <span className="acc-section-icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.9} />
        </span>
        <div>
          <h2 className="acc-section-title">{title}</h2>
          {subtitle && <p className="acc-section-sub">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Spinner({ label }) {
  return (
    <p className="acc-loading" role="status">
      <span className="acc-spinner" aria-hidden="true" />
      {label}
    </p>
  );
}

/* ── signed out ────────────────────────────────────────────────────────── */

function AuthPanel({ oauthError }) {
  const [mode, setMode] = useState('signin'); // signin | signup | forgot
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setNotice('');
  };

  // This hands the whole browser over to Google, so there is no "after" to write
  // code for and nothing to congratulate anyone about yet. The button only ever
  // goes from idle to pending — success happens on a different page load.
  const startGoogle = async () => {
    if (busy || googleBusy) return;
    setGoogleBusy(true);
    setError('');
    setNotice('');
    writeFlag(GOOGLE_PENDING_KEY, '1');
    try {
      await supabaseService.signInWithGoogle();
    } catch (err) {
      clearFlag(GOOGLE_PENDING_KEY);
      setGoogleBusy(false);
      setError(
        'The Google sign-in page wouldn\'t open. Have another go, or use your email and password '
        + 'below.',
      );
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');

    try {
      if (mode === 'forgot') {
        await supabaseService.sendPasswordReset(form.email.trim());
        setNotice(
          'If that email has an account, a reset link is on its way. It can take a minute or two.',
        );
      } else if (mode === 'signup') {
        const result = await supabaseService.signUp({
          email: form.email.trim(),
          password: form.password,
          fullName: form.name.trim(),
          phone: form.phone.trim(),
        });
        // Depending on the Supabase email-confirmation setting, sign-up either
        // returns a live session or nothing until the client clicks the link.
        if (!result?.session) {
          setNotice('Almost there — check your email and click the link to confirm your account.');
        }
      } else {
        await supabaseService.signIn({ email: form.email.trim(), password: form.password });
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const heading =
    mode === 'signup' ? 'Create your account'
      : mode === 'forgot' ? 'Reset your password'
        : 'Sign in';

  return (
    <div className="acc-auth">
      <div className="acc-card acc-auth-card">
        <h1 className="acc-auth-heading">{heading}</h1>

        {mode !== 'forgot' && (
          <div className="acc-toggle" role="group" aria-label="Sign in or create an account">
            <button
              type="button"
              className={`acc-toggle-btn${mode === 'signin' ? ' is-active' : ''}`}
              aria-pressed={mode === 'signin'}
              onClick={() => switchMode('signin')}
            >
              Sign in
            </button>
            <button
              type="button"
              className={`acc-toggle-btn${mode === 'signup' ? ' is-active' : ''}`}
              aria-pressed={mode === 'signup'}
              onClick={() => switchMode('signup')}
            >
              Create account
            </button>
          </div>
        )}

        {oauthError && (
          <p className="acc-alert acc-alert--warn" role="alert">
            <AlertTriangle size={16} strokeWidth={2} aria-hidden="true" />
            <span>{oauthError}</span>
          </p>
        )}

        {mode !== 'forgot' && (
          <>
            <button
              type="button"
              className="acc-google"
              onClick={startGoogle}
              disabled={googleBusy || busy}
            >
              <GoogleMark />
              {googleBusy
                ? 'Taking you to Google…'
                : mode === 'signup' ? 'Continue with Google' : 'Sign in with Google'}
            </button>
            <p className="acc-hint acc-google-note">
              {mode === 'signup'
                ? "Quickest way in if you already use Gmail. No Google account? You don't need one — fill in the form below instead, it works exactly the same."
                : 'Only if you set your account up with Google. Otherwise use your email and password below.'}
            </p>
            <div className="acc-or"><span>or</span></div>
          </>
        )}

        <form className="acc-form" onSubmit={submit}>
          {mode === 'signup' && (
            <>
              <div className="acc-field">
                <label htmlFor="acc-name">Your name</label>
                <input
                  id="acc-name" name="name" type="text" autoComplete="name"
                  value={form.name} onChange={change} required
                  placeholder="Jane Smith"
                />
              </div>
              <div className="acc-field">
                <label htmlFor="acc-phone">Phone</label>
                <input
                  id="acc-phone" name="phone" type="tel" autoComplete="tel"
                  value={form.phone} onChange={change} required
                  placeholder="021 123 4567"
                />
              </div>
            </>
          )}

          <div className="acc-field">
            <label htmlFor="acc-email">Email</label>
            <input
              id="acc-email" name="email" type="email" autoComplete="email"
              value={form.email} onChange={change} required
              placeholder="you@email.com"
            />
            {mode === 'signup' && (
              <p className="acc-hint">
                Use the same email you book with and your past appointments will already be here.
              </p>
            )}
          </div>

          {mode !== 'forgot' && (
            <div className="acc-field">
              <label htmlFor="acc-password">Password</label>
              <input
                id="acc-password" name="password" type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={form.password} onChange={change} required
                minLength={mode === 'signup' ? 8 : undefined}
              />
              {mode === 'signup' && <p className="acc-hint">At least 8 characters.</p>}
            </div>
          )}

          {error && <p className="acc-alert acc-alert--error" role="alert">{error}</p>}
          {notice && <p className="acc-alert acc-alert--ok" role="status">{notice}</p>}

          <button type="submit" className="acc-btn acc-btn--primary" disabled={busy || googleBusy}>
            {busy
              ? 'Just a moment…'
              : mode === 'signup' ? 'Create account'
                : mode === 'forgot' ? 'Send reset link'
                  : 'Sign in'}
          </button>

          <div className="acc-form-links">
            {mode === 'forgot' ? (
              <button type="button" className="acc-link-btn" onClick={() => switchMode('signin')}>
                Back to sign in
              </button>
            ) : (
              <button type="button" className="acc-link-btn" onClick={() => switchMode('forgot')}>
                Forgotten your password?
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="acc-card acc-sell">
        <p className="acc-eyebrow">Why bother</p>
        <h2 className="acc-sell-heading">It saves you a phone call</h2>
        <ul className="acc-sell-list">
          <li>
            <CalendarDays size={17} strokeWidth={1.9} aria-hidden="true" />
            <span>See what you've got booked, and cancel it yourself if life happens.</span>
          </li>
          <li>
            <Clock size={17} strokeWidth={1.9} aria-hidden="true" />
            <span>Rebook your usual in a tap — everything's already filled in.</span>
          </li>
          <li>
            <Gift size={17} strokeWidth={1.9} aria-hidden="true" />
            <span>Tell me your birthday and I'll email you something for it, every year.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

/* ── password recovery ─────────────────────────────────────────────────── */

function NewPasswordPanel({ onDone }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    // Password changes are an auth concern, not a table read, so they go through
    // the auth client directly the same way sign-in does.
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (updateError) setError(updateError.message);
    else onDone();
  };

  return (
    <div className="acc-auth">
      <div className="acc-card acc-auth-card">
        <h1 className="acc-auth-heading">Choose a new password</h1>
        <form className="acc-form" onSubmit={submit}>
          <div className="acc-field">
            <label htmlFor="acc-new-password">New password</label>
            <input
              id="acc-new-password" type="password" autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              required minLength={8}
            />
            <p className="acc-hint">At least 8 characters.</p>
          </div>
          {error && <p className="acc-alert acc-alert--error" role="alert">{error}</p>}
          <button type="submit" className="acc-btn acc-btn--primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save new password'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── where the appointment is ──────────────────────────────────────────── */

// Viktoria works from two buildings depending on the day, and a client who turns
// up at the wrong one has lost their appointment. So the place is on the card,
// not in an email they may not still have.
function BookingWhere({ booking }) {
  const name = booking.location_name;
  const address = booking.location_address;
  if (!name && !address) return null;

  return (
    <p className="acc-booking-where">
      <MapPin size={16} strokeWidth={1.9} aria-hidden="true" />
      <span>
        {name && <span className="acc-booking-place">{name}</span>}
        {address && <span className="acc-booking-address">{address}</span>}
        <a
          className="acc-booking-map"
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || name)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in maps
        </a>
      </span>
    </p>
  );
}

// Set when Viktoria has moved a booking herself. The appointment is confirmed —
// this is the reason it changed, not a question, so it reads like a note rather
// than an alarm.
//
// Her name stays on the citation. Everything else on this page is her talking,
// but these are her exact words about one appointment, quoted, and a quotation
// with nobody's name under it is just floating text.
function RescheduleNote({ message }) {
  if (!message) return null;
  return (
    <blockquote className="acc-quote">
      <p>{message}</p>
      <cite>Viktoria</cite>
    </blockquote>
  );
}

/* ── upcoming appointments ─────────────────────────────────────────────── */

function UpcomingSection({ bookings, awaiting, unsettled, onCancelled }) {
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const cancel = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await supabaseService.cancelMyBooking(id);
      setConfirmId(null);
      await onCancelled();
    } catch (err) {
      setError(err.message || `That didn't cancel. Give me a ring on ${SALON_PHONE} and I'll sort it.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SectionCard icon={CalendarDays} title="Your next appointments">
      {bookings.length === 0 && awaiting.length === 0 && unsettled.length === 0 ? (
        <div className="acc-empty">
          <p className="acc-empty-title">Nothing in the diary yet.</p>
          <p>
            When you book, it'll show up here — and you'll be able to cancel or rebook without
            picking up the phone.
          </p>
          <Link to="/book" className="acc-btn acc-btn--primary acc-btn--inline">
            Book an appointment
          </Link>
        </div>
      ) : (
        <>
          {error && <p className="acc-alert acc-alert--error" role="alert">{error}</p>}

          <ul className="acc-list">
            {bookings.map((b) => (
              <li key={b.id} className="acc-booking">
                <div className="acc-booking-when">
                  <span className="acc-booking-date">{formatDate(b.date)}</span>
                  <span className="acc-booking-time">{formatTime(b.time) || 'Time to be confirmed'}</span>
                </div>

                <p className="acc-booking-treatments">
                  {treatmentNames(b) || 'Appointment'}
                </p>

                <p className="acc-booking-meta">
                  {bookingDuration(b) > 0 && <span>{bookingDuration(b)} min</span>}
                  {bookingPrice(b) && <span>{bookingPrice(b)}</span>}
                </p>

                <BookingWhere booking={b} />
                <RescheduleNote message={b.reschedule_message} />

                {confirmId === b.id ? (
                  <div className="acc-confirm" aria-live="polite">
                    <p>
                      Cancel {formatDate(b.date)} at {formatTime(b.time)}? This can't be undone —
                      you'd need to book a new time.
                    </p>
                    <div className="acc-confirm-actions">
                      <button
                        type="button"
                        className="acc-btn acc-btn--danger"
                        onClick={() => cancel(b.id)}
                        disabled={busyId === b.id}
                      >
                        {busyId === b.id ? 'Cancelling…' : 'Yes, cancel it'}
                      </button>
                      <button
                        type="button"
                        className="acc-btn acc-btn--quiet"
                        onClick={() => setConfirmId(null)}
                        disabled={busyId === b.id}
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="acc-booking-actions">
                    <Link to={`/book?rebooking=${b.id}`} className="acc-btn acc-btn--outline">
                      Book again <ChevronRight size={15} strokeWidth={2} aria-hidden="true" />
                    </Link>
                    <button
                      type="button"
                      className="acc-btn acc-btn--quiet"
                      onClick={() => setConfirmId(b.id)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            ))}

            {awaiting.map((b) => (
              <li key={b.id} className="acc-booking acc-booking--pending">
                <div className="acc-booking-when">
                  <span className="acc-booking-date">{formatDate(b.date)}</span>
                  <span className="acc-booking-time">Awaiting confirmation</span>
                </div>
                <p className="acc-booking-treatments">
                  {treatmentNames(b) || 'Appointment'}
                </p>
                <BookingWhere booking={b} />
                <p className="acc-hint">
                  I haven't confirmed this one yet. I'll email you as soon as I have.
                </p>
              </li>
            ))}

            {/* Left over from the old propose-and-accept flow, which nothing
                creates any more. Rather than let one of these silently vanish
                off the page, it says plainly that it still needs a time. */}
            {unsettled.map((b) => (
              <li key={b.id} className="acc-booking acc-booking--moving">
                <p className="acc-moved-was">
                  <s className="acc-moved-strike">
                    {formatDate(b.date)}{b.time ? ` at ${formatTime(b.time)}` : ''}
                  </s>
                  <span className="acc-moved-was-note">needs a new time</span>
                </p>
                <p className="acc-booking-treatments">
                  {treatmentNames(b) || 'Appointment'}
                </p>
                <RescheduleNote message={b.reschedule_message} />
                <p className="acc-hint">
                  This one was never settled on a new time. Pick whatever suits you and you're
                  sorted — or ring me on {SALON_PHONE}.
                </p>
                <Link to="/book" className="acc-btn acc-btn--primary acc-btn--block">
                  Pick a time that suits
                </Link>
              </li>
            ))}
          </ul>

          <p className="acc-policy">
            <AlertTriangle size={15} strokeWidth={2} aria-hidden="true" />
            <span>
              <strong>24 hours' notice, please.</strong> Cancelling inside 24 hours may attract a
              50% fee, and if it's last minute a quick call to me on {SALON_PHONE} is kinder than
              a click.
            </span>
          </p>
        </>
      )}
    </SectionCard>
  );
}

/* ── visit history ─────────────────────────────────────────────────────── */

function HistorySection({ visits }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? visits : visits.slice(0, 5);

  return (
    <SectionCard icon={Clock} title="Where you've been">
      {visits.length === 0 ? (
        <p className="acc-muted">
          No visits recorded yet. Once you've been in, every appointment and what it cost will be
          listed here.
        </p>
      ) : (
        <>
          <ul className="acc-list acc-list--compact">
            {shown.map((b) => (
              <li key={b.id} className="acc-visit">
                <div className="acc-visit-main">
                  <span className="acc-visit-date">{formatDate(b.date)}</span>
                  <span className="acc-visit-treatments">
                    {treatmentNames(b) || 'Appointment'}
                  </span>
                </div>
                <div className="acc-visit-price">
                  {bookingPrice(b) || '—'}
                  {b.product_cents > 0 && (
                    <span className="acc-visit-extra">
                      plus {formatMoney(b.product_cents)} products
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {visits.length > 5 && (
            <button
              type="button"
              className="acc-btn acc-btn--quiet acc-btn--full"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Show fewer' : `Show all ${visits.length} visits`}
            </button>
          )}
        </>
      )}
    </SectionCard>
  );
}

/* ── first visit ───────────────────────────────────────────────────────── */

// Two holes that only show up once someone actually signs in: Google hands over
// a name and an email and nothing else, so Viktoria has no way to reach half of
// these clients; and nothing on the site has ever asked for a birthday, so the
// birthday email has never once fired.
//
// Both fields are optional and stay optional. Skipping costs the client
// nothing they can see, so the ask has to earn itself in one sentence each.
function WelcomeSection({ profile, fromGoogle, isNew, onSaved }) {
  const askPhone = !(profile.phone || '').trim();
  const askBirthday = !profile.birthday_locked && !(profile.birth_day && profile.birth_month);
  const askingBoth = askPhone && askBirthday;

  const [phone, setPhone] = useState('');
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState(null);
  const outcomeRef = useRef(null);

  // Nothing takes focus when the page loads — this is the first thing a new
  // client sees and they may already be reading it. Focus only moves once they
  // have pressed a button, and only because the thing they were using has just
  // been replaced by this message.
  useEffect(() => {
    if (outcome && outcomeRef.current) outcomeRef.current.focus();
  }, [outcome]);

  const finish = (next) => {
    writePersistent(welcomeDoneKey(profile?.id), '1');
    setOutcome(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    const typedPhone = phone.trim();
    const savingPhone = askPhone && typedPhone !== '';
    const savingBirthday = askBirthday && Boolean(day) && Boolean(month);

    if (savingPhone && typedPhone.replace(/\D/g, '').length < 7) {
      setError("That number looks a few digits short. Have another look, or leave it blank and "
        + "carry on — you can add it later.");
      return;
    }
    if (askBirthday && Boolean(day) !== Boolean(month)) {
      setError('Please pick both a day and a month, or leave both blank.');
      return;
    }
    if (savingBirthday && Number(day) > DAYS_IN_MONTH[Number(month) - 1]) {
      setError(`${MONTHS[Number(month) - 1]} doesn't have ${day} days.`);
      return;
    }

    // Save pressed with both boxes empty is a skip with extra steps.
    if (!savingPhone && !savingBirthday) {
      finish({ kind: 'skipped' });
      return;
    }

    setBusy(true);
    setError('');
    try {
      const updated = await supabaseService.updateMyProfile({
        full_name: profile.full_name || undefined,
        // Left undefined rather than blank when they skipped it: an empty
        // string would overwrite a number they already had.
        phone: savingPhone ? typedPhone : profile.phone || undefined,
        birth_day: savingBirthday ? day : null,
        birth_month: savingBirthday ? month : null,
      });
      onSaved(updated);

      // Read the birthday back rather than trusting what was typed. The
      // protect_profile_fields trigger silently pins an already-set birthday
      // instead of erroring, so the update succeeds and returns the OLD value —
      // set it on another device and this would otherwise cheerfully confirm a
      // month the account does not hold.
      const savedDay = Number(updated?.birth_day) || 0;
      const savedMonth = Number(updated?.birth_month) || 0;
      const birthdayTookEffect =
        savingBirthday && savedDay === Number(day) && savedMonth === Number(month);

      if (savingBirthday && !birthdayTookEffect && savedDay && savedMonth) {
        finish({
          kind: 'saved',
          phone: savingPhone ? typedPhone : '',
          birthMonth: 0,
          birthdayAlreadySet: { day: savedDay, month: savedMonth },
        });
        return;
      }

      finish({
        kind: 'saved',
        phone: savingPhone ? typedPhone : '',
        birthMonth: birthdayTookEffect ? savedMonth : 0,
      });
    } catch (err) {
      setError(err.message || "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (outcome) {
    return (
      <section className="acc-card acc-welcome acc-welcome--done">
        <div className="acc-welcome-done" role="status" tabIndex={-1} ref={outcomeRef}>
          {outcome.kind === 'saved' ? (
            <>
              <p className="acc-welcome-done-title">
                <Check size={18} strokeWidth={2.4} aria-hidden="true" /> Saved — thank you.
              </p>
              <ul className="acc-welcome-done-list">
                {outcome.phone && (
                  <li>I can reach you on {outcome.phone} if anything moves.</li>
                )}
                {outcome.birthMonth > 0 && (
                  <li>
                    I&apos;ll email you something for your birthday, at the start of{' '}
                    {MONTHS[outcome.birthMonth - 1]} each year.
                  </li>
                )}
                {outcome.birthdayAlreadySet && (
                  <li>
                    Your birthday was already saved as{' '}
                    <strong>
                      {outcome.birthdayAlreadySet.day}{' '}
                      {MONTHS[outcome.birthdayAlreadySet.month - 1]}
                    </strong>
                    , and it can only be set once — so I&apos;ve left it as it is. Text me on{' '}
                    <a href="tel:021881498">{SALON_PHONE}</a> if that isn&apos;t right.
                  </li>
                )}
              </ul>
              <p className="acc-hint">It's all under Your details, further down this page.</p>
            </>
          ) : (
            <>
              <p className="acc-welcome-done-title">No problem.</p>
              <p className="acc-muted">
                Nothing here stops you booking. Whenever you want to add{' '}
                {askingBoth ? 'a phone number or your birthday' : askPhone ? 'a phone number' : 'your birthday'},
                it's under Your details, further down this page.
              </p>
            </>
          )}
        </div>
      </section>
    );
  }

  const known = [profile.full_name && 'name', profile.email && 'email'].filter(Boolean).join(' and ');

  return (
    <section className="acc-card acc-welcome" aria-labelledby="acc-welcome-title">
      {/* The same block catches an old account that never had a birthday on it,
          so it only says "first time" when that is actually true. */}
      <p className="acc-eyebrow">{isNew ? 'First time here' : 'Almost done'}</p>
      <h2 className="acc-welcome-title" id="acc-welcome-title">
        {askingBoth ? 'Two quick things' : 'One quick thing'}
      </h2>
      <p className="acc-welcome-lead">
        {isNew && "Your account's all set up. "}
        {askingBoth
          ? "Two things I don't have for you yet — both optional, and neither takes a minute."
          : askPhone
            ? "There's one thing I don't have for you yet."
            : "There's one thing I don't have for you yet — your birthday."}
      </p>

      {known && (
        <p className="acc-welcome-known">
          <Check size={16} strokeWidth={2.4} aria-hidden="true" />
          <span>
            I've got your {known} {fromGoogle ? 'from Google' : 'already'} — nothing to do there.
            <span className="acc-welcome-known-values">
              {[profile.full_name, profile.email].filter(Boolean).join(' · ')}
            </span>
          </span>
        </p>
      )}

      <form className="acc-form acc-welcome-form" onSubmit={submit}>
        {askPhone && (
          <div className="acc-welcome-ask">
            <div className="acc-field">
              <label htmlFor="acc-welcome-phone">
                <Phone size={15} strokeWidth={2} aria-hidden="true" /> Your phone number
              </label>
              <p className="acc-welcome-why" id="acc-welcome-phone-why">
                So I can reach you about your appointments — a text if I'm running behind, or if a
                time has to move.
                {fromGoogle && " Signing in with Google doesn't bring a number across."}
              </p>
              <input
                id="acc-welcome-phone"
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="021 123 4567"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setError(''); }}
                aria-describedby="acc-welcome-phone-why"
              />
            </div>
          </div>
        )}

        {askBirthday && (
          <div className="acc-welcome-ask">
            <fieldset className="acc-welcome-fieldset">
              {/* The icon sits in a span, not straight in the legend: browsers
                  disagree about honouring `display` on a legend itself. */}
              <legend>
                <span className="acc-welcome-legend">
                  <Gift size={15} strokeWidth={2} aria-hidden="true" /> Your birthday
                </span>
              </legend>
              <p className="acc-welcome-why">
                I'll email you something for your birthday month, every year. Day and month
                only — I never ask for the year, and I don't want it. It does one job: that
                email. No marketing, nothing shared with anyone.
              </p>
              <div className="acc-birthday-inputs">
                <div className="acc-field">
                  <label htmlFor="acc-welcome-day">Day</label>
                  <select
                    id="acc-welcome-day"
                    value={day}
                    onChange={(e) => { setDay(e.target.value); setError(''); }}
                  >
                    <option value="">–</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="acc-field">
                  <label htmlFor="acc-welcome-month">Month</label>
                  <select
                    id="acc-welcome-month"
                    value={month}
                    onChange={(e) => { setMonth(e.target.value); setError(''); }}
                  >
                    <option value="">–</option>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* The same fact as the note in the details section, pointed the
                  other way round. There it answers "why can't I change this?".
                  Here, above the Save button, the anti-fraud version would be
                  accusing someone who has done nothing — so this one says the
                  useful half: it is a one-off, and it is fixable by a person. */}
              <p className="acc-hint acc-welcome-lock">
                You'll only ever need to set this once. Have a quick look before you save, and if
                it does end up wrong, text me on {SALON_PHONE} and I'll sort it.
              </p>
            </fieldset>
          </div>
        )}

        {error && <p className="acc-alert acc-alert--error" role="alert">{error}</p>}

        <div className="acc-welcome-actions">
          <button type="submit" className="acc-btn acc-btn--primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="acc-link-btn"
            onClick={() => finish({ kind: 'skipped' })}
            disabled={busy}
          >
            Skip for now
          </button>
        </div>
      </form>
    </section>
  );
}

/* ── details ───────────────────────────────────────────────────────────── */

function DetailsSection({ profile, onSaved }) {
  const hasBirthday = Boolean(profile.birth_day && profile.birth_month);
  const birthdayFixed = hasBirthday || Boolean(profile.birthday_locked);

  // Held locally rather than mirrored from props, so a background refresh
  // elsewhere on the page never wipes out half-typed edits.
  const [form, setForm] = useState({
    full_name: profile.full_name || '',
    phone: profile.phone || '',
    birth_day: profile.birth_day ? String(profile.birth_day) : '',
    birth_month: profile.birth_month ? String(profile.birth_month) : '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // The welcome block at the top of the page saves the same row, so a number
  // added up there has to show up down here. Only fields the client has not
  // touched are taken across — anything half-typed is left exactly as it is.
  const syncedProfile = useRef(profile);
  useEffect(() => {
    const previous = syncedProfile.current;
    if (previous === profile) return;
    syncedProfile.current = profile;
    const carry = (current, was, now) => (current === (was || '') ? now || '' : current);
    setForm((f) => ({
      full_name: carry(f.full_name, previous.full_name, profile.full_name),
      phone: carry(f.phone, previous.phone, profile.phone),
      birth_day: carry(
        f.birth_day,
        previous.birth_day ? String(previous.birth_day) : '',
        profile.birth_day ? String(profile.birth_day) : '',
      ),
      birth_month: carry(
        f.birth_month,
        previous.birth_month ? String(previous.birth_month) : '',
        profile.birth_month ? String(profile.birth_month) : '',
      ),
    }));
  }, [profile]);

  const change = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setSaved(false);
  };

  const maxDay = form.birth_month ? DAYS_IN_MONTH[Number(form.birth_month) - 1] : 31;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    if (!birthdayFixed && Boolean(form.birth_day) !== Boolean(form.birth_month)) {
      setError('Please choose both a day and a month for your birthday, or leave both blank.');
      return;
    }
    if (!birthdayFixed && form.birth_day && Number(form.birth_day) > maxDay) {
      setError(`${MONTHS[Number(form.birth_month) - 1]} doesn't have ${form.birth_day} days.`);
      return;
    }

    setBusy(true);
    setError('');
    try {
      const updated = await supabaseService.updateMyProfile({
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        birth_day: birthdayFixed ? null : form.birth_day,
        birth_month: birthdayFixed ? null : form.birth_month,
      });
      setSaved(true);
      onSaved(updated);
    } catch (err) {
      setError(err.message || "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Signing in with Google brings a name across and nothing else, so plenty of
  // accounts will land here with no phone number at all. Say so rather than
  // leaving an innocuous empty box.
  const phoneMissing = !form.phone.trim();

  return (
    <SectionCard
      icon={User}
      title="Your details"
      subtitle={phoneMissing ? "I don't have a phone number for you yet" : undefined}
    >
      <form className="acc-form" onSubmit={submit}>
        <div className="acc-field">
          <label htmlFor="acc-detail-name">Name</label>
          <input
            id="acc-detail-name" name="full_name" type="text" autoComplete="name"
            value={form.full_name} onChange={change} required
          />
        </div>

        <div className="acc-field">
          <label htmlFor="acc-detail-phone">
            Phone
            {phoneMissing && <span className="acc-field-flag"> — I don't have one</span>}
          </label>
          <input
            id="acc-detail-phone" name="phone" type="tel" autoComplete="tel"
            value={form.phone} onChange={change}
            aria-describedby={phoneMissing ? 'acc-detail-phone-why' : undefined}
          />
          {phoneMissing && (
            <p className="acc-hint acc-hint--warn" id="acc-detail-phone-why">
              Pop one in — it's how I reach you if anything changes on the day, and signing in
              with Google doesn't pass a number across.
            </p>
          )}
        </div>

        <div className="acc-field">
          <span className="acc-field-label">Email</span>
          <p className="acc-static">{profile.email || '—'}</p>
          <p className="acc-hint">
            Your email is how appointments are matched to your account. To change it, email me at{' '}
            <a href={`mailto:${SALON_EMAIL}`}>{SALON_EMAIL}</a>.
          </p>
        </div>

        <fieldset className="acc-fieldset">
          <legend>Birthday</legend>

          {birthdayFixed ? (
            <>
              <p className="acc-static">
                {hasBirthday ? `${profile.birth_day} ${MONTHS[profile.birth_month - 1]}` : 'Not recorded'}
              </p>
              <p className="acc-hint">
                {hasBirthday && (
                  <>
                    I email you something for it at the start of{' '}
                    {MONTHS[profile.birth_month - 1]} each year.{' '}
                  </>
                )}
                This one is fixed now — birthdays lock once saved, so nobody can move theirs
                around to get more than one birthday treat a year. If it's wrong, text me on{' '}
                {SALON_PHONE} and I'll change it for you.
              </p>
            </>
          ) : (
            <>
              <p className="acc-hint acc-hint--lead">
                Worth ten seconds: give me the day and month and I'll email you something for
                your birthday month, every year. I never ask for the year you were born — I
                don't need it. It does one job: that email. No marketing, nothing shared with
                anyone.
              </p>
              <div className="acc-birthday-inputs">
                <div className="acc-field">
                  <label htmlFor="acc-birth-day">Day</label>
                  <select
                    id="acc-birth-day" name="birth_day" value={form.birth_day} onChange={change}
                  >
                    <option value="">–</option>
                    {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="acc-field">
                  <label htmlFor="acc-birth-month">Month</label>
                  <select
                    id="acc-birth-month" name="birth_month" value={form.birth_month} onChange={change}
                  >
                    <option value="">–</option>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Still the pre-save moment, so it is worded like the welcome
                  card. The reason birthdays lock at all is a sentence further
                  down, on the branch where it is a question the client is
                  actually asking. */}
              <p className="acc-hint">
                You'll only ever need to set this once, so have a quick look before you save. If
                it does end up wrong, text me on {SALON_PHONE} and I'll sort it.
              </p>
            </>
          )}
        </fieldset>

        {error && <p className="acc-alert acc-alert--error" role="alert">{error}</p>}
        {saved && <p className="acc-alert acc-alert--ok" role="status">Saved.</p>}

        <button type="submit" className="acc-btn acc-btn--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save details'}
        </button>
      </form>
    </SectionCard>
  );
}

/* ── page ──────────────────────────────────────────────────────────────── */

function Account({ session, authLoading }) {
  const [recovery, setRecovery] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [partialError, setPartialError] = useState('');
  const [data, setData] = useState({ profile: null, bookings: [] });
  const [oauthError, setOauthError] = useState(OAUTH_ENTRY.error);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const oauthChecked = useRef(false);

  // A password-reset link signs the client in, which would otherwise drop them
  // straight onto their account with no way to actually set a new password.
  // Coming back from Google lands on the same page by the same route, so both
  // are read off the URL here and out of the auth events.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
      // The session from Google has just been picked up out of the URL. App.js
      // already has it and will re-render this page signed in; all that is left
      // here is to drop any earlier "it didn't work".
      if (event === 'SIGNED_IN') setOauthError('');
    });
    if (window.location.hash.includes('type=recovery')) setRecovery(true);
    return () => subscription.unsubscribe();
  }, []);

  // A failed sign-in leaves ?error=… or #error=… sitting in the address bar,
  // where a refresh would show the same message again over a page that is
  // otherwise fine.
  useEffect(() => {
    if (!OAUTH_ENTRY.error) return;
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }, []);

  // Back from Google, nothing in the URL to explain it, and still nobody signed
  // in. Something failed quietly, and a client staring at a sign-in form they
  // thought they had just got past deserves a sentence about it.
  useEffect(() => {
    if (oauthChecked.current || authLoading) return;
    oauthChecked.current = true;
    if (OAUTH_ENTRY.started && !session && !OAUTH_ENTRY.error) setOauthError(GOOGLE_FAILED);
  }, [authLoading, session]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setLoadError('');
    setPartialError('');

    const results = await Promise.allSettled([
      supabaseService.getMyProfile(),
      supabaseService.getMyBookings(),
    ]);

    // One failing call shouldn't blank the page — a profile that loaded is
    // still worth showing even if the appointments didn't, and vice versa.
    if (results.every((r) => r.status === 'rejected')) {
      setLoadError('Your account didn\'t load just now. Check your connection and try again.');
    } else {
      const [profile, bookings] = results;
      setData({
        profile: profile.status === 'fulfilled' ? profile.value : null,
        bookings: bookings.status === 'fulfilled' ? bookings.value || [] : [],
      });
      if (results.some((r) => r.status === 'rejected')) {
        setPartialError('Some of your account didn\'t load. Refresh the page to try again.');
      }
    }

    setLoading(false);
    setRefreshing(false);
  }, []);

  // Keyed on the user id, not the session object: a token refresh hands back a
  // new session every hour and would otherwise refetch the whole page.
  const userId = session?.user?.id;
  useEffect(() => {
    if (userId && !recovery) load();
  }, [userId, recovery, load]);

  // Decided once, the first time a profile lands, and then left alone: the block
  // saves the very fields it is asking about, so a condition read straight off
  // `profile` would rip it off the screen mid-sentence the moment it succeeded.
  // Closing it is the block's own business.
  useEffect(() => {
    const p = data.profile;
    if (!p || welcomeOpen) return;
    if (readPersistent(welcomeDoneKey(p.id)) === '1') return;
    const missingPhone = !(p.phone || '').trim();
    const missingBirthday = !p.birthday_locked && !(p.birth_day && p.birth_month);
    if (missingPhone || missingBirthday) setWelcomeOpen(true);
  }, [data.profile, welcomeOpen]);

  const handleProfileSaved = (updated) =>
    setData((d) => ({ ...d, profile: updated || d.profile }));

  if (authLoading) {
    return (
      <div className="acc-page">
        <div className="acc-shell">
          <Spinner
            label={OAUTH_ENTRY.started
              ? 'Finishing your Google sign-in…'
              : 'Loading your account…'}
          />
        </div>
      </div>
    );
  }

  if (recovery) {
    return (
      <div className="acc-page">
        <div className="acc-shell">
          <NewPasswordPanel onDone={() => setRecovery(false)} />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="acc-page">
        <div className="acc-shell"><AuthPanel oauthError={oauthError} /></div>
      </div>
    );
  }

  const { profile, bookings } = data;
  const firstName = (profile?.full_name || '').trim().split(' ')[0];
  const newAccount = isNewAccount(profile?.created_at);

  // Viktoria moves a booking outright now, so a moved appointment is simply an
  // approved one on a new date. `rescheduled` is only ever historical: nothing
  // creates it any more, but anything still carrying it has to stay visible.
  const unsettled = bookings
    .filter((b) => b.upcoming && b.status === 'rescheduled')
    .sort(sortByWhen);
  const upcoming = bookings
    .filter((b) => b.upcoming && b.status === 'approved')
    .sort(sortByWhen);
  const awaiting = bookings
    .filter((b) => b.upcoming && b.status === 'pending')
    .sort(sortByWhen);
  const visits = bookings
    .filter((b) => !b.upcoming && b.status === 'approved')
    .sort((a, b) => sortByWhen(b, a));

  return (
    <div className="acc-page">
      <div className="acc-shell">
        <header className="acc-header">
          <div>
            <p className="acc-eyebrow">Your account</p>
            {/* "Welcome" on a brand new account, so the greeting and the ask
                below it read as one thought rather than saying the client's
                name twice in a row. Everyone else keeps "Hello". */}
            <h1 className="acc-title">
              {welcomeOpen && newAccount
                ? (firstName ? `Welcome, ${firstName}` : 'Welcome')
                : (firstName ? `Hello, ${firstName}` : 'Hello')}
            </h1>
          </div>
          <button
            type="button"
            className="acc-btn acc-btn--quiet"
            onClick={() => supabaseService.signOut()}
          >
            <LogOut size={16} strokeWidth={1.9} aria-hidden="true" /> Sign out
          </button>
        </header>

        {loading ? (
          <Spinner label="Fetching your appointments…" />
        ) : loadError ? (
          <div className="acc-card acc-error-card">
            <p className="acc-alert acc-alert--error" role="alert">{loadError}</p>
            <button type="button" className="acc-btn acc-btn--primary" onClick={() => load()}>
              Try again
            </button>
            <p className="acc-hint">
              Still stuck? Ring me on {SALON_PHONE} — I can see your bookings from my end.
            </p>
          </div>
        ) : (
          <>
            {partialError && (
              <p className="acc-alert acc-alert--warn" role="status">{partialError}</p>
            )}
            {refreshing && <p className="acc-refreshing" role="status">Updating…</p>}

            {welcomeOpen && profile && (
              <WelcomeSection
                profile={profile}
                fromGoogle={session?.user?.app_metadata?.provider === 'google'}
                isNew={newAccount}
                onSaved={handleProfileSaved}
              />
            )}

            <UpcomingSection
              bookings={upcoming}
              awaiting={awaiting}
              unsettled={unsettled}
              onCancelled={() => load({ silent: true })}
            />

            <HistorySection visits={visits} />

            {profile ? (
              <DetailsSection key={profile.id} profile={profile} onSaved={handleProfileSaved} />
            ) : (
              <SectionCard icon={User} title="Your details">
                <p className="acc-muted">
                  Your details didn't load. Refresh the page, or email me at{' '}
                  <a href={`mailto:${SALON_EMAIL}`}>{SALON_EMAIL}</a>.
                </p>
              </SectionCard>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Account;
